import React, { useRef, useEffect, useState } from "react";
import { Box, Typography, alpha, SxProps, Theme, IconButton, TextField, InputAdornment } from "@mui/material";
import { Close, Search, KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";
import { TranscriptData } from "./TranscriptViewer";
import { HighlightedText } from "./HighlightedText";
import { useTranscriptSearch } from "./useTranscriptSearch";

interface TranscriptOverlayProps {
  transcript: TranscriptData;
  currentTimeStamp: number;
  handleParagraphClick: (timestamp: number) => void;
  sx?: SxProps<Theme>;
}

const speakerColors = [
  "#90CAF9", // Blue 200
  "#A5D6A7", // Green 200
  "#CE93D8", // Purple 200
  "#FFCC80", // Orange 200
  "#81D4FA", // Light Blue 200
  "#B39DDB", // Deep Purple 200
];

const formatTimestamp = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export const TranscriptOverlay: React.FC<TranscriptOverlayProps> = ({
  transcript,
  currentTimeStamp,
  handleParagraphClick,
  sx,
}) => {
  const scrollBoxRef = useRef<HTMLDivElement | null>(null);
  const [userIsScrolling, setUserIsScrolling] = useState(false);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const {
    searchQuery, setSearchQuery, matches, matchCount,
    currentMatchIndex, clearSearch, isSearchActive,
    getMatchesForParagraph, goToNextMatch, goToPreviousMatch
  } = useTranscriptSearch(transcript);

  const getSpeakerColor = (speaker: string | number): string => {
    const speakerIndex = typeof speaker === "number" ? speaker : parseInt(speaker, 10) || 0;
    return speakerColors[speakerIndex % speakerColors.length];
  };

  const handleScrollEvent = () => {
    setUserIsScrolling(true);
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }
    scrollTimeout.current = setTimeout(() => {
      setUserIsScrolling(false);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
      scrollTimeout.current = null;
    }, 5000); // 5 second resume delay for overlay as it is smaller
  };

  useEffect(() => {
    return () => {
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, []);


  return (
    <Box
      ref={scrollBoxRef}
      onScroll={handleScrollEvent}
      sx={{
        position: 'absolute',
        top: 16,
        left: 16,
        bottom: 48, // Space for standard controls if any
        width: '300px',
        maxWidth: '40%',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
        borderRadius: 2,
        overflowY: 'auto',
        p: 2,
        zIndex: 10,
        ...(sx as any),
        maskImage: 'linear-gradient(to bottom, black 85%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to bottom, black 85%, transparent 100%)',
        // Scrollbar styling
        '&::-webkit-scrollbar': {
            width: '4px',
        },
        '&::-webkit-scrollbar-track': {
            background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(255, 255, 255, 0.5)',
        },
      }}
    >
      {/* Search bar */}
      <Box sx={{
        position: 'sticky',
        top: -16, // offset the parent padding
        zIndex: 1,
        backgroundColor: 'rgba(0,0,0,0.8)',
        backdropFilter: 'blur(4px)',
        borderRadius: 1,
        mb: 1,
        mx: -0.5,
        px: 1,
        py: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}>
        <Search sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', flexShrink: 0 }} />
        <TextField
          size="small"
          variant="standard"
          placeholder="Search transcript..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (e.shiftKey) {
                goToPreviousMatch();
              } else {
                goToNextMatch();
              }
            }
          }}
          sx={{
            flex: 1,
            '& .MuiInput-root': { color: 'white', fontSize: '0.8rem' },
            '& .MuiInput-underline:before': { borderColor: 'rgba(255,255,255,0.2)' },
            '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderColor: 'rgba(255,255,255,0.4)' },
            '& .MuiInput-underline:after': { borderColor: 'rgba(255,255,255,0.6)' },
          }}
          slotProps={{
            input: {
              endAdornment: isSearchActive && (
                <InputAdornment position="end">
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap', fontSize: '0.7rem', mr: 0.5 }}>
                    {matchCount > 0 ? `${currentMatchIndex + 1}/${matchCount}` : "0"}
                  </Typography>
                  {matchCount > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      <IconButton
                        size="small"
                        onClick={goToPreviousMatch}
                        sx={{ p: 0, color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' } }}
                      >
                        <KeyboardArrowUp sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={goToNextMatch}
                        sx={{ p: 0, color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' } }}
                      >
                        <KeyboardArrowDown sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  )}
                </InputAdornment>
              ),
            },
          }}
        />
        {searchQuery && (
          <IconButton size="small" onClick={clearSearch} sx={{ color: 'rgba(255,255,255,0.5)', p: 0.25, '&:hover': { color: 'white' } }}>
            <Close sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>

      {transcript.paragraphs && transcript.paragraphs.map((paragraph, index) => {
        const isActive =
          currentTimeStamp >= paragraph.start &&
          currentTimeStamp <= paragraph.end;

        return (
          <Box
            key={index}
            id={`transcript-overlay-paragraph-${index}`}
            onClick={() => handleParagraphClick(paragraph.start)}
            ref={(el: HTMLDivElement | null) => {
              if (isSearchActive && el && matches[currentMatchIndex]?.paragraphIndex === index && scrollBoxRef.current) {
                const container = scrollBoxRef.current;
                const elementTop = el.offsetTop - container.offsetTop;
                const elementHeight = el.clientHeight;
                const containerHeight = container.clientHeight;
                container.scrollTo({
                  top: elementTop - containerHeight / 2 + elementHeight / 2,
                  behavior: 'smooth',
                });
              } else if (!isSearchActive && el && isActive && !userIsScrolling && scrollBoxRef.current) {
                const container = scrollBoxRef.current;
                const elementTop = el.offsetTop - container.offsetTop;
                const elementHeight = el.clientHeight;
                const containerHeight = container.clientHeight;
                container.scrollTo({
                    top: elementTop - containerHeight / 2 + elementHeight / 2,
                    behavior: 'smooth'
                });
              }
            }}
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: isActive
                ? alpha('#ffffff', 0.15)
                : "transparent",
              borderLeft: isActive
                ? `3px solid #ffffff`
                : "3px solid transparent",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: alpha('#ffffff', 0.1),
              },
            }}
          >
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography
                variant="caption"
                sx={{
                    fontWeight: 700,
                    color: getSpeakerColor(paragraph.speaker),
                    textShadow: '0px 1px 2px rgba(0,0,0,0.8)'
                }}
              >
                Speaker {paragraph.speaker}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace' }}>
                {formatTimestamp(paragraph.start)}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              component="div"
              sx={{
                color: '#ffffff',
                lineHeight: 1.4,
                textShadow: '0px 1px 1px rgba(0,0,0,0.8)'
              }}
            >
              <HighlightedText
                text={paragraph.sentences.map((s) => s.text).join(" ")}
                matches={getMatchesForParagraph(index)}
                currentMatchOffset={
                  matches[currentMatchIndex]?.paragraphIndex === index
                    ? matches[currentMatchIndex].startOffset
                    : undefined
                }
                highlightColor="rgba(255, 235, 59, 0.3)"
                activeHighlightColor="rgba(255, 235, 59, 0.6)"
              />
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
